import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { motion, Variants } from "framer-motion";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTranslation } from "react-i18next";
import ContactMeetingDialog from "../contact/ContactMeetingDialog";
import ContactMessageDialog from "../contact/ContactMessageDialog";

const BLOCK: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const ITEM: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.2, ease: [0.16, 0.77, 0.47, 0.97] },
  },
};

const viewport = { once: true, amount: 0.1 };

const BOX_ICONS = [CalendarMonthOutlinedIcon, EditOutlinedIcon];

const ContactSection: React.FC = () => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);

  const BOXES = [
    {
      id: "meeting",
      label: t("contact.meeting.label"),
      sublabel: t("contact.meeting.sublabel"),
      icon: BOX_ICONS[0],
    },
    {
      id: "form",
      label: t("contact.form.label"),
      sublabel: t("contact.form.sublabel"),
      icon: BOX_ICONS[1],
    },
  ];

  const handleAction = (id: string) => {
    if (id === "meeting") setMeetingDialogOpen(true);
    if (id === "form") setDialogOpen(true);
  };

  return (
    <>
      <Box
        component="section"
        id="contact"
        sx={{
          bgcolor: "#f8f9fb",
          position: "relative",
          borderBottom: "1px solid rgba(29,47,65,0.15)",
        }}
      >
        <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 3, md: 6 } }}>
          {/* Heading */}
          <Box
            sx={{
              py: { xs: 6, md: 10 },
              borderBottom: "1px solid rgba(29,47,65,0.15)",
            }}
          >
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              variants={BLOCK}
            >
              <motion.div variants={ITEM} style={{ willChange: "transform" }}>
                <Typography
                  sx={{
                    fontSize: { xs: "1.8rem", md: "2.8rem" },
                    fontWeight: 700,
                    letterSpacing: "-0.025em",
                    lineHeight: 1.2,
                    color: "#1D2F41",
                    fontFamily: '"Segoe UI Semibold", "Segoe UI", sans-serif',
                  }}
                >
                  {t("contact.heading")}
                  <br />
                  {t("contact.subheading")}
                </Typography>
              </motion.div>
            </motion.div>
          </Box>

          {/* Two boxes */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={BLOCK}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
                py: { xs: 6, md: 8 },
              }}
            >
              {BOXES.map((box) => {
                const Icon = box.icon;
                return (
                  <motion.div
                    key={box.id}
                    variants={ITEM}
                    style={{ willChange: "transform", flex: 1 }}
                  >
                    <Box
                      onClick={() => handleAction(box.id)}
                      sx={{
                        flex: 1,
                        border: "1px solid rgba(29,47,65,0.12)",
                        p: 4,
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                        cursor: "pointer",
                        transition: "border-color 0.2s, background 0.2s",
                        "&:hover": {
                          borderColor: "rgba(29,47,65,0.3)",
                          bgcolor: "rgba(29,47,65,0.02)",
                        },
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: "1.5rem",
                          color: "rgba(29,47,65,0.45)",
                        }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "1.1rem",
                            fontWeight: 600,
                            color: "#1D2F41",
                            fontFamily:
                              '"Segoe UI Semibold", "Segoe UI", sans-serif',
                            mb: 0.75,
                          }}
                        >
                          {box.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: 300,
                            color: "rgba(29,47,65,0.55)",
                            fontFamily: '"Calibri", sans-serif',
                          }}
                        >
                          {box.sublabel}
                        </Typography>
                      </Box>
                    </Box>
                  </motion.div>
                );
              })}
            </Box>
          </motion.div>
        </Box>
      </Box>

      <ContactMeetingDialog open={meetingDialogOpen} onClose={() => setMeetingDialogOpen(false)} calendlyId="calendly-inline-section" />
      <ContactMessageDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
};

export default ContactSection;
